import { TextField, type TextFieldProps } from '@mui/material';
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ForwardRefExoticComponent,
  type MouseEventHandler,
  type PropsWithoutRef,
  type ReactNode,
  type RefAttributes,
} from 'react';
import * as stylex from '@stylexjs/stylex';
import { TextareaPrimitive, type TextareaPrimitiveProps, Label as FplLabel } from '@figma/fpl-components';
import { borderRadius, color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { toStringValue } from '@/UI/Input/utils';

export interface NodeTextFieldProps extends Omit<TextFieldProps, 'multiline'> {
  /** Always multiline; `false` is rejected so the two branches cannot diverge. */
  multiline?: true;
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  /**
   * Legacy `.MuiOutlinedInput-root` styles. The FPL branch ignores everything here except `pt`,
   * which it forwards to `Textarea.Root` so callers can keep controlling the field's top padding.
   */
  rootSx?: Record<string, unknown>;
  /** Applies in both branches. */
  maxLength?: number;
  /** The `<textarea>` itself, in both branches, e.g. to read or restore the selection. */
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  /** Read only by the FPL branch; the legacy branch ignores the whole bucket. */
  fplProps?: {
    /** Visible label wired via `htmlFor`. Flag-on, FPL chrome no longer dims read-only fields. */
    label?: ReactNode;
  };
}

type Styleable = { className?: string; style?: CSSProperties };
const StyledTextareaPrimitive = TextareaPrimitive as unknown as ForwardRefExoticComponent<
  PropsWithoutRef<TextareaPrimitiveProps & Styleable> & RefAttributes<HTMLTextAreaElement>
>;

const styles = stylex.create({
  /**
   * Root is the field container + scroller (mirrors legacy's `.MuiOutlinedInput-root`). The primitive
   * is unstyled, so we re-create FPL `Textarea`'s chrome here — background, border/focus ring, radius —
   * and own the padding. An unresized node has no height (`DynamicNode2`), so the textarea grows with
   * its content; a resized one is bounded and Root scrolls.
   */
  root: {
    // Deliberately not a flex container: the textarea is `flex: 1`, so a flex Root would size it by
    // the flex algorithm and override the inline height `expandable` writes, killing auto-grow. Block
    // Root leaves the textarea free to grow and lets Root scroll when bounded.
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    boxSizing: 'border-box',
    backgroundColor: fplColor.bgSecondary,
    borderRadius: borderRadius.medium,
    outlineWidth: 1,
    outlineStyle: 'solid',
    outlineColor: {
      default: 'transparent',
      ':hover:not(:focus-within)': fplColor.border,
      ':focus-within': fplColor.borderSelected,
    },
    outlineOffset: -1,
    paddingTop: spacer.spacer3,
    paddingBottom: spacer.spacer3,
    paddingInline: spacer.spacer3,
  },
  /** Read-only / disabled fields drop the elevated background and show a resting border, matching FPL. */
  rootMuted: {
    backgroundColor: fplColor.bg,
    // Resting/hover border stays constant (the `default` covers hover); focus still elevates.
    outlineColor: {
      default: fplColor.border,
      ':focus-within': fplColor.borderSelected,
    },
  },
  /** Per-component cursor hatch, inherited by the textarea via `var(--fpl-input-cursor, text)`. */
  readOnlyCursor: {
    '--fpl-input-cursor': 'inherit',
  },
  /** Overrides Root's top padding with the caller's legacy `rootSx.pt` (replaces, never adds). */
  rootPaddingTop: (paddingTop: string | number) => ({ paddingTop }),
  /** The typeable area: transparent so Root's chrome shows through, carrying `text.body.large`. */
  textarea: {
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    resize: 'none',
    color: fplColor.text,
    cursor: 'var(--fpl-input-cursor, text)',
    '::placeholder': {
      color: fplColor.textTertiary,
      opacity: 1,
    },
  },
  textareaDisabled: {
    color: fplColor.textDisabled,
  },
  labelledField: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
    flex: 1,
    minHeight: 0,
  },
});

/** Legacy MUI implementation, rendered when `fpl-migration` is off. Must stay untouched. */
function LegacyNodeTextField({
  sx,
  rootSx,
  slotProps,
  textColor,
  maxLength,
  inputRef,
  textareaRef,
  onFocus,
  onBlur,
  ...props
}: Omit<NodeTextFieldProps, 'color' | 'minHeight' | 'maxHeight' | 'multiline'> & {
  textColor: NodeTextFieldProps['color'];
  inputRef: React.MutableRefObject<HTMLElement | null>;
}) {
  const legacyInput = (slotProps?.input ?? {}) as Record<string, unknown>;
  const legacyInputProps = (legacyInput.inputProps ?? {}) as Record<string, unknown>;

  return (
    <TextField
      {...props}
      inputRef={textareaRef}
      multiline
      onFocus={onFocus}
      onBlur={onBlur}
      slotProps={{
        ...slotProps,
        input: {
          ...legacyInput,
          readOnly: props.readOnly,
          ref: inputRef,
          style: {
            fontSize: '16px',
            fontWeight: 400,
            color: textColor || fplColor.text,
          },
          inputProps: {
            ...legacyInputProps,
            ...(maxLength != null ? { maxLength } : {}),
          },
        },
      }}
      sx={{
        overflow: 'hidden',
        bgcolor: color.Black84,
        borderColor: color.White04_T,
        borderRadius: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          padding: '24px',
          flex: 1,
          alignItems: 'stretch',
          overflowY: 'auto',
          border: `1px solid ${color.White04_T}`,
          '& fieldset': {
            display: 'none',
          },
          '& textarea::placeholder': {
            color: fplColor.textSecondary,
          },
          ...rootSx,
          '& textarea': {
            resize: 'none',
            ...(rootSx?.['& textarea'] ?? {}),
          },
        },
        ...sx,
      }}
    />
  );
}

/**
 * Resolves a legacy `rootSx.pt` into a CSS length. Numbers follow MUI's spacing scale (the theme
 * doesn't override the default 8px factor), so `pt: 5` becomes `40px`; strings pass through as-is.
 */
function resolvePaddingTop(pt: unknown): string | number | undefined {
  if (typeof pt === 'number') return pt * 8;
  if (typeof pt === 'string') return pt;
  return undefined;
}

/**
 * Renders on FPL's unstyled `TextareaPrimitive` so this branch owns both the field chrome (see
 * `styles.root`) and the exact `spacer3` padding, with `text.body.large` on the textarea. The legacy
 * MUI escape hatches (`sx`, `slotProps`, `fullWidth`, `color`) are intentionally dropped and stay on
 * the flag-off path. `rootSx` is the one exception: its `pt` is forwarded to Root so callers keep
 * control of the field's top padding.
 */
function FplNodeTextField({
  id,
  value,
  placeholder,
  readOnly,
  disabled,
  required,
  name,
  autoFocus,
  maxLength,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onPaste,
  onMouseDown,
  textareaRef,
  fplProps,
  rootSx,
  'aria-label': ariaLabel,
}: Pick<
  NodeTextFieldProps,
  | 'id'
  | 'value'
  | 'placeholder'
  | 'readOnly'
  | 'disabled'
  | 'required'
  | 'name'
  | 'autoFocus'
  | 'maxLength'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'onKeyDown'
  | 'onPaste'
  | 'aria-label'
  | 'fplProps'
  | 'rootSx'
> & {
  onMouseDown: MouseEventHandler<HTMLDivElement> | undefined;
  textareaRef: (node: HTMLTextAreaElement | null) => void;
}) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const localRef = useRef<HTMLTextAreaElement | null>(null);

  // FPL re-fits an expandable textarea on keystroke and mount only, so a value arriving from
  // upstream (a connected node, collab) would leave a stale height. Re-fit on value change too.
  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (el.scrollHeight > 0) {
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);

  const setRef = (node: HTMLTextAreaElement | null) => {
    localRef.current = node;
    textareaRef(node);
  };

  // Value-first FPL onChange -> MUI event-first, so callers keep their handlers.
  const handleChange: NonNullable<TextareaPrimitiveProps['onChange']> = (_value, opts) => {
    onChange?.(opts.event);
  };

  // MUI types these for the field root, the primitive for the textarea; at runtime it's the
  // textarea event.
  const domHandlers = { onFocus, onBlur, onKeyDown, onPaste } as unknown as Pick<
    TextareaPrimitiveProps,
    'onFocus' | 'onBlur' | 'onKeyDown' | 'onPaste'
  >;

  // Match MUI's disabled semantics. `onMouseDown` is excluded on purpose: it lives on Root and must
  // keep stopping propagation to the canvas for locked nodes (see StringNode).
  const interactionHandlers = disabled ? undefined : { onChange: handleChange, ...domHandlers };

  const paddingTop = resolvePaddingTop(rootSx?.pt);

  const textareaSkin = stylex.props(
    typographyStyles.textBodyLarge,
    styles.textarea,
    disabled && styles.textareaDisabled,
  );

  const field = (
    <TextareaPrimitive.Root
      {...stylex.props(
        styles.root,
        (readOnly || disabled) && styles.rootMuted,
        readOnly && styles.readOnlyCursor,
        paddingTop != null && styles.rootPaddingTop(paddingTop),
      )}
      onMouseDown={onMouseDown}
    >
      <StyledTextareaPrimitive
        ref={setRef}
        id={inputId}
        aria-label={ariaLabel}
        className={textareaSkin.className}
        style={textareaSkin.style}
        rows={1}
        // Matches legacy's `TextareaAutosize`: the field grows with its content and Root scrolls.
        expandable
        value={toStringValue(value)}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        name={name}
        autoFocus={autoFocus}
        maxLength={maxLength}
        spellCheck={readOnly ? false : undefined}
        {...interactionHandlers}
      />
    </TextareaPrimitive.Root>
  );

  if (!fplProps?.label) {
    return field;
  }

  return (
    <div {...stylex.props(styles.labelledField)}>
      <FplLabel htmlFor={inputId}>{fplProps.label}</FplLabel>
      {field}
    </div>
  );
}

/**
 * Text field for canvas nodes. Owns what neither MUI nor FPL know about — `nodrag` and wheel
 * containment so scrolling the field doesn't zoom the canvas — and delegates the field itself.
 */
export const NodeTextField = ({
  sx,
  rootSx,
  slotProps,
  color: textColor,
  minHeight,
  maxHeight,
  maxLength,
  multiline: _multiline,
  fplProps,
  textareaRef,
  ...props
}: NodeTextFieldProps) => {
  const isFplEnabled = useFplMigrationFlag();
  const inputRef = useRef<HTMLElement | null>(null);
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const [isFocused, setIsFocused] = useState(false);

  useCaptureElementWheelScroll(isHovered || isFocused ? inputRef : { current: null });

  const handleFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    props.onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    props.onBlur?.(event);
  };

  return (
    <FlexCol
      sx={{
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: minHeight ?? 0,
        maxHeight,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="nodrag"
    >
      {isFplEnabled ? (
        <FplNodeTextField
          {...props}
          maxLength={maxLength}
          fplProps={fplProps}
          rootSx={rootSx}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseDown={props.onMouseDown}
          textareaRef={(node) => {
            // Watch the scroller, as the legacy branch does with `.MuiOutlinedInput-root`. Here that
            // is `TextareaPrimitive.Root`, the textarea's immediate parent — `expandable` sizes the
            // textarea to its content, so it never reports itself as scrollable and wheel events would
            // reach the canvas. Reach the Root through the DOM instead of wiring a second ref.
            inputRef.current = node?.parentElement ?? null;
            if (textareaRef) {
              textareaRef.current = node;
            }
          }}
        />
      ) : (
        <LegacyNodeTextField
          {...props}
          sx={sx}
          rootSx={rootSx}
          slotProps={slotProps}
          textColor={textColor}
          maxLength={maxLength}
          inputRef={inputRef}
          textareaRef={textareaRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      )}
    </FlexCol>
  );
};
